/**
 * Notification badge component for header
 */

import { memo } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface NotificationBadgeProps {
  count: number;
  onClick?: () => void;
  className?: string;
}

export const NotificationBadge = memo(function NotificationBadge({
  count,
  onClick,
  className,
}: NotificationBadgeProps) {
  const hasUnread = count > 0;
  const displayCount = count > 99 ? '99+' : count;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClick}
            className={cn(
              'relative hover:bg-muted/80 text-muted-foreground',
              className
            )}
            aria-label={`Notifications${hasUnread ? `, ${count} unread` : ''}`}
          >
            <Bell className="h-5 w-5" />
            {hasUnread && (
              <>
                <span
                  className={cn(
                    'absolute top-1.5 right-1.5 flex items-center justify-center',
                    'min-w-[18px] h-[18px] px-1',
                    'bg-destructive text-destructive-foreground',
                    'text-[10px] font-semibold rounded-full',
                    'ring-2 ring-background',
                    'animate-pulse'
                  )}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {displayCount}
                </span>
              </>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end">
          <p>
            {hasUnread
              ? `You have ${count} unread notification${count === 1 ? '' : 's'}`
              : 'No unread notifications'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
});
